import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AlbumPhoto } from '@/features/album/types'
import { DEFAULT_SLOT_COUNT, MAT_COLORS } from '../catalog'
import { getGallerySlotCount } from '../galleryMeshCatalog'
import {
  WIZARD_STEPS,
  type GalleryPrivacy,
  type WizardDraft,
  type WizardPrintSlot,
  type WizardStepId,
} from '../types'

const storageKey = (albumId: string) => `ew-gallery-wizard:${albumId}`

function slotsFromPhotos(photos: AlbumPhoto[], max = DEFAULT_SLOT_COUNT): WizardPrintSlot[] {
  const slots: WizardPrintSlot[] = []
  for (let i = 0; i < max; i++) {
    const photo = photos[i]
    slots.push(
      photo
        ? {
            index: i,
            photoId: photo.id,
            egoId: photo.egoId,
            thumbUrl: photo.thumbUrl || photo.url,
            url: photo.url,
            excluded: false,
          }
        : { index: i, excluded: false },
    )
  }
  return slots
}

/**
 * Keep existing placements, then backfill empty slots with album photos
 * that aren't already on the board (by photoId or egoId).
 */
function syncSlotsWithPhotos(
  prev: WizardPrintSlot[],
  photos: AlbumPhoto[],
  max: number,
): WizardPrintSlot[] {
  const usedPhotoIds = new Set(prev.map((s) => s.photoId).filter(Boolean) as string[])
  const usedEgoIds = new Set(prev.map((s) => s.egoId).filter(Boolean) as string[])
  const available = photos.filter(
    (p) => !usedPhotoIds.has(p.id) && !(p.egoId && usedEgoIds.has(p.egoId)),
  )

  let availIdx = 0
  const next: WizardPrintSlot[] = []

  for (let i = 0; i < max; i++) {
    const existing = prev[i]
    if (existing?.photoId) {
      next.push({ ...existing, index: i })
      continue
    }
    const photo = available[availIdx++]
    if (photo) {
      next.push({
        index: i,
        photoId: photo.id,
        egoId: photo.egoId,
        thumbUrl: photo.thumbUrl || photo.url,
        url: photo.url,
        excluded: false,
      })
    } else {
      next.push({ index: i, excluded: false })
    }
  }
  return next
}

/** Keep photo assignments when scene slot count changes */
function resizeSlots(prev: WizardPrintSlot[], photos: AlbumPhoto[], max: number): WizardPrintSlot[] {
  return syncSlotsWithPhotos(prev, photos, max)
}

function loadDraft(albumId: string): Partial<WizardDraft> | null {
  try {
    const raw = localStorage.getItem(storageKey(albumId))
    if (!raw) return null
    return JSON.parse(raw) as Partial<WizardDraft>
  } catch {
    return null
  }
}

function saveDraft(draft: WizardDraft) {
  try {
    localStorage.setItem(storageKey(draft.albumId), JSON.stringify(draft))
  } catch {
    /* ignore quota */
  }
}

export function useWizardState(
  albumId: string,
  photos: AlbumPhoto[],
  albumTitle: string,
  initialStep?: WizardStepId,
) {
  const [step, setStep] = useState<WizardStepId>(() => {
    if (initialStep && WIZARD_STEPS.includes(initialStep)) return initialStep
    const saved = loadDraft(albumId)
    if (saved?.step && WIZARD_STEPS.includes(saved.step)) return saved.step
    return 'intent'
  })

  const [sceneId, setSceneId] = useState<string | number | undefined>()
  const [frameId, setFrameId] = useState<string | number | undefined>()
  const [colorId, setColorId] = useState<string | undefined>()
  const [materialId, setMaterialId] = useState<string | number | undefined>()
  const [matWidth, setMatWidth] = useState(0.08)
  const [matColor, setMatColor] = useState(MAT_COLORS[0].hex)
  const [slots, setSlots] = useState<WizardPrintSlot[]>(() => slotsFromPhotos(photos))
  const [galleryTitle, setGalleryTitle] = useState(`${albumTitle} · Gallery`)
  const [privacy, setPrivacy] = useState<GalleryPrivacy>('private')
  const [hydrated, setHydrated] = useState(false)

  // Hydrate from localStorage once, then merge with live album photos
  useEffect(() => {
    const saved = loadDraft(albumId)
    const max =
      saved?.sceneId != null
        ? getGallerySlotCount(saved.sceneId)
        : photos.length
          ? Math.max(DEFAULT_SLOT_COUNT, photos.length)
          : DEFAULT_SLOT_COUNT

    if (saved) {
      if (saved.sceneId != null) setSceneId(saved.sceneId)
      if (saved.frameId != null) setFrameId(saved.frameId)
      if (saved.colorId) setColorId(saved.colorId)
      if (saved.materialId != null) setMaterialId(saved.materialId)
      if (typeof saved.matWidth === 'number') setMatWidth(saved.matWidth)
      if (saved.matColor) setMatColor(saved.matColor)
      if (saved.galleryTitle) setGalleryTitle(saved.galleryTitle)
      if (saved.privacy) setPrivacy(saved.privacy)

      const base = saved.slots?.length ? saved.slots : []
      const slotMax = saved.sceneId != null ? getGallerySlotCount(saved.sceneId) : Math.max(base.length, max)
      setSlots(
        photos.length
          ? syncSlotsWithPhotos(base, photos, slotMax)
          : base.length
            ? base
            : slotsFromPhotos([], slotMax),
      )
    } else if (photos.length) {
      setSlots(slotsFromPhotos(photos, max))
      setGalleryTitle(`${albumTitle} · Gallery`)
    }
    setHydrated(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumId])

  // Backfill empty slots whenever album photos load/update (don't freeze on stale draft)
  useEffect(() => {
    if (!hydrated || !photos.length) return
    setSlots((prev) => {
      const max =
        sceneId != null
          ? getGallerySlotCount(sceneId)
          : Math.max(prev.length || DEFAULT_SLOT_COUNT, Math.min(photos.length, 18))
      const next = syncSlotsWithPhotos(prev, photos, max)
      // Avoid churn if nothing changed
      const same =
        next.length === prev.length &&
        next.every(
          (s, i) =>
            s.photoId === prev[i]?.photoId &&
            s.egoId === prev[i]?.egoId &&
            s.excluded === prev[i]?.excluded,
        )
      return same ? prev : next
    })
  }, [photos, hydrated, sceneId])

  // Resize print board when scene (template) changes — mesh count is template-specific
  useEffect(() => {
    if (!hydrated || sceneId == null) return
    const max = getGallerySlotCount(sceneId)
    setSlots((prev) => {
      if (prev.length === max) {
        // Still backfill empties for this room size
        return syncSlotsWithPhotos(prev, photos, max)
      }
      return resizeSlots(prev, photos, max)
    })
  }, [sceneId, hydrated, photos])

  const draft: WizardDraft = useMemo(
    () => ({
      albumId,
      step,
      sceneId,
      frameId,
      colorId,
      materialId,
      matWidth,
      matColor,
      slots,
      galleryTitle,
      privacy,
    }),
    [
      albumId,
      step,
      sceneId,
      frameId,
      colorId,
      materialId,
      matWidth,
      matColor,
      slots,
      galleryTitle,
      privacy,
    ],
  )

  useEffect(() => {
    if (!hydrated) return
    saveDraft(draft)
  }, [draft, hydrated])

  const stepIndex = WIZARD_STEPS.indexOf(step)

  const canContinue = useMemo(() => {
    switch (step) {
      case 'intent':
        return photos.length > 0
      case 'scene':
        return sceneId != null
      case 'frame':
        // Frame is optional — "No frame" is a valid choice (undefined / __none__)
        return true
      case 'color':
        return Boolean(colorId)
      case 'material':
        return materialId != null
      case 'prints':
        return slots.some((s) => s.photoId && !s.excluded)
      case 'matting':
        return true
      case 'review':
        return galleryTitle.trim().length > 0
      default:
        return false
    }
  }, [step, photos.length, sceneId, frameId, colorId, materialId, slots, galleryTitle])

  const goNext = useCallback(() => {
    const i = WIZARD_STEPS.indexOf(step)
    if (i < WIZARD_STEPS.length - 1) setStep(WIZARD_STEPS[i + 1])
  }, [step])

  const goBack = useCallback(() => {
    const i = WIZARD_STEPS.indexOf(step)
    if (i > 0) setStep(WIZARD_STEPS[i - 1])
  }, [step])

  const goTo = useCallback((s: WizardStepId) => setStep(s), [])

  const toggleSlotExcluded = useCallback((index: number) => {
    setSlots((prev) =>
      prev.map((s) => (s.index === index ? { ...s, excluded: !s.excluded } : s)),
    )
  }, [])

  const moveSlot = useCallback((from: number, to: number) => {
    setSlots((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next.map((s, i) => ({ ...s, index: i }))
    })
  }, [])

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey(albumId))
    } catch {
      /* ignore */
    }
  }, [albumId])

  return {
    draft,
    step,
    stepIndex,
    canContinue,
    sceneId,
    setSceneId,
    frameId,
    setFrameId,
    colorId,
    setColorId,
    materialId,
    setMaterialId,
    matWidth,
    setMatWidth,
    matColor,
    setMatColor,
    slots,
    setSlots,
    toggleSlotExcluded,
    moveSlot,
    galleryTitle,
    setGalleryTitle,
    privacy,
    setPrivacy,
    goNext,
    goBack,
    goTo,
    clearDraft,
  }
}
